import React from "react";
import { createShortLink } from "actions/shortLink";
import { fetchMerchantAccountTaxRates } from "actions/taxRate";
import { fetchMerchantAccountShippingRates } from "actions/shippingRate";
import {
  fetchMerchantAccountProductsPrices,
  fetchMerchantAccountCouponsPromotionCodes
} from "actions/merchantAccount";
import { fetchAppCheckout } from "actions/app";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Formik, useFormikContext } from "formik";
import Padder from "components/Padder";
import ProductsFields from "components/ShortLinkForm/Fields/Products";
import CouponsFields from "components/ShortLinkForm/Fields/Coupons";
import ShippingFields from "components/ShortLinkForm/Fields/Shipping";
import TaxRatesFields from "components/ShortLinkForm/Fields/TaxRates";
import PaymentFields from "components/ShortLinkForm/Fields/Payment";
import ReceiverFields from "components/ShortLinkForm/Fields/Receiver";
import RedirectsFields from "components/ShortLinkForm/Fields/Redirects";
import { getShortLinkPreviewTitle } from "utils/shortLink";
import { shortLinkCheckoutPreview } from "components/FormerEditor/common/preview";
import {
  ensureAppCheckout,
  ensureProductsPricesCouponsPromotionCodes,
  ensureTaxRates,
  ensureAttachments,
  ensureShippingRates
} from "utils/hooks";
import { fetchAttachments } from "actions/attachment";
import MenuNavHeader from "components/MenuNavHeader";
import get from "lodash/get";
import compact from "lodash/compact";
import { THEMES } from "utils/constants/ui";
import isEmpty from "lodash/isEmpty";
import InfoList from "components/InfoList";
import CheckoutDetails from "components/SaleForm/Fields/CheckoutDetails";
import {
  prepareCombinationResultInitialValues,
  validateCreateCombinationResult
} from "utils/combination";
import { STATE_KEYS } from "utils/constants/state";
import { composeCheckoutConfigsToShortLink } from "utils/checkout/merge";
import ValidationsBar from "components/ValidationsBar";
import { VALIDATION_CHECKOUT_KEY } from "constants/validation";
import { TAX_ID_COLLECTION_TYPE_STANDARD_OPTION } from "utils/constants/taxRate";
import { SHORT_LINK_NODE_KEY } from "utils/constants/shortLink";

const CheckoutBuilder = ({ actions, goBack, uuid, name, activeIndex }) => {
  ensureAppCheckout({
    fetchAppCheckout: actions.fetchAppCheckout
  });

  const {
    values: parentValues,
    setValues: setParentValues
  } = useFormikContext();
  const checkoutPath = `${name}.${STATE_KEYS.PRODUCT_COLLECTION.CONFIG_PRODUCTS_CHECKOUTS}.${uuid}`;
  const currentValues = get(parentValues, checkoutPath);

  const commonCheckoutPath = `${name}.${STATE_KEYS.PRODUCT_COLLECTION.CONFIG_CHECKOUT_COMMON}`;
  const checkoutCommon = get(parentValues, commonCheckoutPath);

  const {
    products,
    prices,
    coupons
  } = ensureProductsPricesCouponsPromotionCodes({
    fetchMerchantAccountProductsPrices:
      actions.fetchMerchantAccountProductsPrices,
    fetchMerchantAccountCouponsPromotionCodes:
      actions.fetchMerchantAccountCouponsPromotionCodes
  });
  const { taxRates } = ensureTaxRates({
    fetchMerchantAccountTaxRates: actions.fetchMerchantAccountTaxRates
  });
  const { shippingRates } = ensureShippingRates({
    fetchMerchantAccountShippingRates: actions.fetchMerchantAccountShippingRates
  });
  ensureAttachments({
    fetchAttachments: actions.fetchAttachments
  });

  const submitForm = (formValues, formikActions) => {
    goBack(formValues.data.product);
    formikActions.setSubmitting(false);
  };

  /**
   * We incorporate common checkout config into initial values so we have full validations
   */
  const initialValues = prepareCombinationResultInitialValues(currentValues);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitForm}
      validate={validateCreateCombinationResult({
        prices,
        products,
        prices,
        coupons,
        taxRates,
        shippingRates,
        common: checkoutCommon
      })}
    >
      {({ values, submitForm, isSubmitting, errors }) => {
        const commonResources = {
          products,
          prices,
          coupons,
          taxRates,
          shippingRates
        };
        /**
         * TODO:
         * - should this mirror
         * - src/components/FormerEditor/BlockSettings/FormBlock/CheckoutSettings/CombinationCheckoutBuilder/index.jsx
         * - depends do product collections yet support a default constant checkout?
         */
        const previewInput = {
          shortLink: composeCheckoutConfigsToShortLink({
            selectedCheckout: values.data.product,
            commonConfig: checkoutCommon
          }),
          ...commonResources
        };
        const checkoutPreview = shortLinkCheckoutPreview(previewInput);
        const previewSubtotal = getShortLinkPreviewTitle(previewInput);

        const hasValidationMessages = errors && errors.messages;

        const validationNode = hasValidationMessages ? (
          <ValidationsBar
            validations={errors.messages}
            noun="checkout"
            context={VALIDATION_CHECKOUT_KEY.COMBINATIONS}
            values={parentValues}
            setValues={setParentValues}
          />
        ) : null;

        return (
          <div className="w-100">
            <MenuNavHeader
              label={previewSubtotal}
              customClasses={{
                container:
                  "flex flex-column justify-between items-end sticky-l bb hairline-1 bg-white bh--inset top-0 z-5",
                content: "pb3 ph3 pl1 pt3 w-100 flex flex-row",
                label: "fw6 f3 word-break"
              }}
              action={{
                onClick: submitForm,
                copy: `Save`,
                disabled: !isEmpty(errors),
                loading: isSubmitting
              }}
              footer={validationNode}
            />
            <div className="pa3">
              {compact([
                messagesList
                  ? {
                      key: SHORT_LINK_NODE_KEY.MESSAGES,
                      node: messagesList
                    }
                  : null,
                {
                  key: SHORT_LINK_NODE_KEY.PRODUCTS,
                  node: <ProductsFields />
                },
                {
                  key: SHORT_LINK_NODE_KEY.PAYMENT,
                  node: <PaymentFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.TAX,
                  node: (
                    <TaxRatesFields
                      name={STATE_KEYS.SHORT_LINK.CHECKOUT}
                      collectionTypeOptions={[
                        TAX_ID_COLLECTION_TYPE_STANDARD_OPTION
                      ]}
                    />
                  )
                },
                {
                  key: SHORT_LINK_NODE_KEY.COUPONS,
                  node: <CouponsFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.SHIPPING,
                  node: <ShippingFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.CHECKOUT_PREVIEW,
                  node: (
                    <CheckoutDetails
                      header="Checkout preview"
                      collapseImages={checkoutPreview.collapseImages}
                      {...checkoutPreview.checkout}
                    />
                  )
                }
              ]).map(({ node, key }, nodeIx) => (
                <div className={nodeIx ? "pt3 pt4-ns" : ""} key={key}>
                  {node}
                </div>
              ))}
            </div>
            <Padder theme="visible" />
          </div>
        );
      }}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      createShortLink,
      fetchAppCheckout,
      fetchMerchantAccountProductsPrices,
      fetchMerchantAccountCouponsPromotionCodes,
      fetchMerchantAccountTaxRates,
      fetchMerchantAccountShippingRates,
      fetchAttachments
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(CheckoutBuilder);
